import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../services/api';
import {
  DocumentTextIcon,
  ClockIcon,
  UserCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
} from '@heroicons/react/24/outline';

interface DashboardStats {
  totalCases: number;
  totalApprovedCases: number;
  totalVictims: number;
  primaryVsSecondary: { primary: number; secondary: number };
  perStatus: Record<string, number>;
  perIpriRegion: { LAC: number; ASIA: number; AFRICA: number; unclassified: number };
  approvedThisYear: { year: number; total: number; LAC: number; ASIA: number; AFRICA: number };
  approvedLastYear: { year: number; total: number; LAC: number; ASIA: number; AFRICA: number };
}

interface RecentCase {
  id: string;
  caseNumber: string;
  incidentDate: string;
  country: string;
  region?: string;
  status: string;
  createdAt: string;
}

interface RecentActivity {
  id: string;
  action: string;
  entityType: string;
  createdAt: string;
  user: {
    firstName: string;
    lastName: string;
    role: string;
  };
  case?: {
    id: string;
    caseNumber: string;
  };
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  DRAFT: { label: 'Draft', color: 'text-gray-700', bg: 'bg-gray-400' },
  FOR_VALIDATION: { label: 'For Validation', color: 'text-blue-700', bg: 'bg-blue-500' },
  IN_RESEARCH: { label: 'In Research', color: 'text-yellow-700', bg: 'bg-yellow-500' },
  FOR_APPROVAL: { label: 'For Approval', color: 'text-purple-700', bg: 'bg-purple-500' },
  APPROVED: { label: 'Approved', color: 'text-green-700', bg: 'bg-green-500' },
  REJECTED: { label: 'Rejected', color: 'text-red-700', bg: 'bg-red-500' },
  ARCHIVED: { label: 'Archived', color: 'text-gray-500', bg: 'bg-gray-300' },
};

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentCases, setRecentCases] = useState<RecentCase[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, casesResponse, activityResponse] = await Promise.all([
        API.get('/api/dashboard/stats'),
        API.get('/api/cases?limit=10'),
        API.get('/api/audit-logs?limit=15')
      ]);

      setStats(statsResponse.data);
      setRecentCases(casesResponse.data.cases || []);
      setRecentActivity(activityResponse.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      try {
        const statsResponse = await API.get('/api/dashboard/stats');
        setStats(statsResponse.data);
      } catch (statsError) {
        console.error('Failed to fetch stats:', statsError);
      }
    } finally {
      setLoading(false);
    }
  };

  const getYoyChange = () => {
    if (!stats) return null;
    const thisYear = stats.approvedThisYear.total;
    const lastYear = stats.approvedLastYear.total;
    if (lastYear === 0) return thisYear > 0 ? { pct: 100, direction: 'up' as const } : { pct: 0, direction: 'flat' as const };
    const pct = Math.round(((thisYear - lastYear) / lastYear) * 100);
    return { pct: Math.abs(pct), direction: pct > 0 ? 'up' as const : pct < 0 ? 'down' as const : 'flat' as const };
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-gray-200 dark:bg-gray-600 rounded-lg" />)}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {[...Array(2)].map((_, i) => <div key={i} className="h-64 bg-gray-200 dark:bg-gray-600 rounded-lg" />)}
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const yoy = getYoyChange();
  const statusEntries = Object.entries(stats.perStatus).filter(([, count]) => count > 0);
  const statusTotal = statusEntries.reduce((sum, [, count]) => sum + count, 0);
  const regionEntries: [string, number][] = [
    ['LAC', stats.perIpriRegion.LAC],
    ['Asia', stats.perIpriRegion.ASIA],
    ['Africa', stats.perIpriRegion.AFRICA],
  ];
  const regionTotal = regionEntries.reduce((sum, [, count]) => sum + count, 0) + stats.perIpriRegion.unclassified;

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 font-heading">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Overview of human rights cases and system activity
        </p>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-8">
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 rounded-md p-3">
              <DocumentTextIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Cases</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{stats.totalCases}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-3">
              <ArrowTrendingUpIcon className="h-6 w-6 text-white" />
            </div>
            <div className="ml-5">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Approved ({stats.approvedThisYear.year})</p>
              <div className="flex items-center gap-2">
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{stats.approvedThisYear.total}</p>
                {yoy && yoy.direction !== 'flat' && (
                  <span className={`inline-flex items-center text-xs font-medium ${yoy.direction === 'up' ? 'text-red-600' : 'text-green-600'}`}>
                    {yoy.direction === 'up' ? <ArrowTrendingUpIcon className="h-3 w-3 mr-0.5" /> : <ArrowTrendingDownIcon className="h-3 w-3 mr-0.5" />}
                    {yoy.pct}%
                  </span>
                )}
                {yoy && yoy.direction === 'flat' && (
                  <span className="inline-flex items-center text-xs font-medium text-gray-400">
                    <MinusIcon className="h-3 w-3 mr-0.5" />
                    0%
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-400">vs {stats.approvedLastYear.total} in {stats.approvedLastYear.year}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-5">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Primary vs Secondary</p>
          <div className="flex items-end gap-4">
            <div>
              <p className="text-2xl font-semibold text-green-600">{stats.primaryVsSecondary.primary}</p>
              <p className="text-xs text-gray-400">Primary</p>
            </div>
            <div>
              <p className="text-2xl font-semibold text-yellow-600">{stats.primaryVsSecondary.secondary}</p>
              <p className="text-xs text-gray-400">Secondary</p>
            </div>
          </div>
        </div>
      </div>

      {/* Status Pipeline & Region Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        {/* Status Breakdown */}
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 font-heading mb-4">Cases by Status</h3>
          {/* Status bar */}
          <div className="flex rounded-full overflow-hidden h-3 mb-4">
            {statusEntries.map(([status, count]) => (
              <div
                key={status}
                className={`${STATUS_CONFIG[status]?.bg || 'bg-gray-300'}`}
                style={{ width: `${(count / statusTotal) * 100}%` }}
                title={`${STATUS_CONFIG[status]?.label || status}: ${count}`}
              />
            ))}
          </div>
          <div className="space-y-2">
            {statusEntries.map(([status, count]) => {
              const cfg = STATUS_CONFIG[status];
              return (
                <div key={status} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${cfg?.bg || 'bg-gray-300'}`} />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{cfg?.label || status}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Region Breakdown */}
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 font-heading mb-4">Cases by IPRI Region</h3>
          <div className="flex rounded-full overflow-hidden h-3 mb-4">
            {regionEntries.map(([region, count], i) => (
              <div
                key={region}
                className={i === 0 ? 'bg-emerald-500' : i === 1 ? 'bg-teal-400' : 'bg-cyan-400'}
                style={{ width: regionTotal > 0 ? `${(count / regionTotal) * 100}%` : '0%' }}
                title={`${region}: ${count}`}
              />
            ))}
          </div>
          <div className="space-y-2">
            {regionEntries.map(([region, count], i) => (
              <div key={region} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2.5 h-2.5 rounded-full ${i === 0 ? 'bg-emerald-500' : i === 1 ? 'bg-teal-400' : 'bg-cyan-400'}`} />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{region}</span>
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{count}</span>
              </div>
            ))}
            {stats.perIpriRegion.unclassified > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-300" />
                  <span className="text-sm text-gray-400">Unclassified</span>
                </div>
                <span className="text-sm font-medium text-gray-400">{stats.perIpriRegion.unclassified}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Cases */}
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 font-heading">
              Recent Cases
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Latest case submissions
            </p>
          </div>
          <div className="overflow-hidden">
            <div className="max-h-96 overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Case
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {recentCases.map((case_) => (
                    <tr key={case_.id}>
                      <td className="px-4 py-4 text-sm">
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">{case_.caseNumber}</div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {new Date(case_.incidentDate).toLocaleDateString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        <div>
                          <div>{case_.country}</div>
                          {case_.region && <div className="text-xs text-gray-400">{case_.region}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          case_.status === 'APPROVED'
                            ? 'bg-green-100 text-green-800'
                            : case_.status === 'FOR_APPROVAL'
                            ? 'bg-purple-100 text-purple-800'
                            : case_.status === 'IN_RESEARCH'
                            ? 'bg-yellow-100 text-yellow-800'
                            : case_.status === 'REJECTED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {case_.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <Link
                          to={`/cases/${case_.id}`}
                          className="text-emerald-600 hover:text-emerald-900 font-medium"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-900/50 rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 font-heading">
              <ClockIcon className="h-5 w-5 inline mr-2" />
              Recent Activity
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Latest system activity and changes
            </p>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <div className="px-4 py-4 space-y-4">
              {recentActivity.length === 0 ? (
                <div className="text-center py-6 text-gray-500 dark:text-gray-400">
                  <ClockIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                  <p className="text-sm">No recent activity</p>
                </div>
              ) : (
                recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <UserCircleIcon className="h-6 w-6 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {activity.user.firstName} {activity.user.lastName}
                        </span>
                        <span className="text-gray-500 dark:text-gray-400"> {activity.action.toLowerCase()}d </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{activity.entityType}</span>
                        {activity.case && (
                          <span className="text-gray-500 dark:text-gray-400"> for </span>
                        )}
                        {activity.case && (
                          <Link
                            to={`/cases/${activity.case.id}`}
                            className="text-emerald-600 hover:text-emerald-900 font-medium"
                          >
                            {activity.case.caseNumber}
                          </Link>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          activity.user.role === 'ADMIN' ? 'bg-purple-100 text-purple-800' :
                          activity.user.role === 'APPROVER' ? 'bg-blue-100 text-blue-800' :
                          activity.user.role === 'RESEARCHER' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {activity.user.role}
                        </span>
                        <span className="ml-2">
                          {new Date(activity.createdAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
